import {
    createContext,
    type FC,
    type ReactNode,
    useCallback,
    useState,
} from "react";
import { Sidebar, UIFloatingPortal } from "@onshopfront/components";
import { useShopfrontDispatch, useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import { LAYOUT_TOGGLE_MENU } from "#/Actions/General/LayoutActions.ts";
import { NavigationSubMenu } from "#/Layout/Navigation/NavigationSubMenu.tsx";
import { fireEventType } from "#/Utilities/Generic/ActionsHelper.ts";
import { uppercaseFirst } from "#/Utilities/Generic/Strings.ts";
import UUID from "#/Utilities/Generic/UUID.ts";
import { useShowMenu, useShowNavigation } from "#/Utilities/Hooks/ShowMenu.ts";
import { getOption } from "#/Utilities/Platform/Options/Options.ts";

interface Props {
    items: ReactNode;
}

export const NAVIGATION_USER_UPDATE = "navigationUserUpdate";

export type SubMenuNavigationCallback = (label: string, items: ReactNode) => void;

export const NavigationSubNavigationContext = createContext<SubMenuNavigationCallback>(() => {
    throw new TypeError("Cannot navigate to sub-menu outside of Navigation");
});

interface SubMenuType {
    id: string;
    label: string;
    items: ReactNode;
    closing: boolean;
}

export const Navigation: FC<Props> = ({ items }) => {
    const dispatch = useShopfrontDispatch();

    const [ subMenus, setSubMenus ] = useState<Array<SubMenuType>>([]);

    const forceMenu = useShowMenu();
    const open = useShopfrontSelector(state => state.layout.menu.open);

    const showNavigation = useShowNavigation();

    const onClose = useCallback(() => {
        dispatch(fireEventType(LAYOUT_TOGGLE_MENU));
    }, [ dispatch ]);

    const onSubMenuNavigation = useCallback<SubMenuNavigationCallback>((label, items) => {
        setSubMenus(prevState => [
            ...prevState,
            {
                id     : UUID.generate(),
                closing: false,
                label,
                items,
            },
        ]);
    }, []);

    const onPopSubMenu = useCallback(() => {
        setSubMenus(prevState => {
            const lastIndex = prevState.findLastIndex(s => !s.closing);

            if(lastIndex === -1) {
                return prevState;
            }

            return [
                ...prevState.slice(0, lastIndex),
                { ...prevState[lastIndex], closing: true },
                ...prevState.slice(lastIndex + 1),
            ];
        });
    }, []);

    const onClosed = useCallback((id: string) => {
        setSubMenus(prevState => prevState.filter(s => s.id !== id));
    }, []);

    const showOverlay = !forceMenu && open;

    const handleClose = useCallback(() => {
        if(subMenus.some(s => !s.closing)) {
            onPopSubMenu();

            return;
        }

        onClose();
    }, [ onClose, onPopSubMenu, subMenus ]);

    return (
        <>
            <NavigationSubNavigationContext value={onSubMenuNavigation}>
                <UIFloatingPortal>
                    <Sidebar
                        open={showNavigation}
                        onClose={showNavigation ? handleClose : undefined}
                        side="left"
                        className="z-1 bg-white dark:bg-dark-900"
                        wrapperClassName="flex flex-col md:w-[500px] w-screen max-w-full h-full"
                        showOverlay={showOverlay}
                        top={50}
                    >
                        <nav className="w-full h-full pointer-events-auto relative overflow-hidden">
                            <div className="flex flex-col w-full h-full overflow-auto">
                                {items}
                                <div className="text-xs pb-4 px-8 text-neutral-400 dark:text-neutral-500">
                                    <div>Channel: {uppercaseFirst(__VERSION__.channel)}</div>
                                    <div>Version: {__VERSION__.commit}</div>
                                    <div>Device: {getOption("_instance_id")}</div>
                                </div>
                            </div>
                            {subMenus.map(subMenu => (
                                <NavigationSubMenu
                                    key={subMenu.id}
                                    showNavigation={showNavigation}
                                    closing={subMenu.closing}
                                    label={subMenu.label}
                                    onBack={handleClose}
                                    onClosed={() => onClosed(subMenu.id)}
                                >
                                    {subMenu.items}
                                </NavigationSubMenu>
                            ))}
                        </nav>
                    </Sidebar>
                </UIFloatingPortal>
            </NavigationSubNavigationContext>
        </>
    );
};
